/* ==========================================================================
   Customer Account JS – Modern Redesign
   Lebensquelle Plus
   ========================================================================== */

const selectors = {
  customerAddresses: '[data-customer-addresses]',
  addressCountrySelect: '[data-address-country-select]',
  addressContainer: '[data-address]',
  toggleAddressButton: 'button[aria-expanded]',
  cancelAddressButton: 'button[type="reset"]',
  deleteAddressButton: 'button[data-confirm-message]',
};

const attributes = {
  expanded: 'aria-expanded',
  confirmMessage: 'data-confirm-message',
};

class CustomerAddresses {
  constructor() {
    this.elements = this._getElements();
    if (Object.keys(this.elements).length === 0) return;
    this._setupCountries();
    this._setupEventListeners();
  }

  _getElements() {
    const container = document.querySelector(selectors.customerAddresses);
    return container
      ? {
          container,
          addressContainer: container.querySelector(selectors.addressContainer),
          toggleButtons: document.querySelectorAll(selectors.toggleAddressButton),
          cancelButtons: container.querySelectorAll(selectors.cancelAddressButton),
          deleteButtons: container.querySelectorAll(selectors.deleteAddressButton),
          countrySelects: container.querySelectorAll(selectors.addressCountrySelect),
        }
      : {};
  }

  _setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
        hideElement: 'AddressProvinceContainerNew',
      });
      this.elements.countrySelects.forEach((select) => {
        const formId = select.dataset.formId;
        new Shopify.CountryProvinceSelector('AddressCountry_' + formId, 'AddressProvince_' + formId, {
          hideElement: 'AddressProvinceContainer_' + formId,
        });
      });
    }
  }

  _setupEventListeners() {
    this.elements.toggleButtons.forEach((el) => el.addEventListener('click', this._handleAddEditButtonClick));
    this.elements.cancelButtons.forEach((el) => el.addEventListener('click', this._handleCancelButtonClick));
    this.elements.deleteButtons.forEach((el) => el.addEventListener('click', this._handleDeleteButtonClick));
  }

  _toggleExpanded(target) {
    target.setAttribute(attributes.expanded, (target.getAttribute(attributes.expanded) === 'false').toString());
  }

  _handleAddEditButtonClick = ({ currentTarget }) => {
    this._toggleExpanded(currentTarget);
    const card = currentTarget.closest('.lq-address-card');
    if (card) {
      const isExpanded = currentTarget.getAttribute(attributes.expanded) === 'true';
      card.classList.toggle('is-editing', isExpanded);
      if (isExpanded) {
        requestAnimationFrame(() => card.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
      }
    }
  };

  _handleCancelButtonClick = ({ currentTarget }) => {
    const container = currentTarget.closest(selectors.addressContainer);
    if (!container) return;
    const button = container.querySelector('[' + attributes.expanded + ']');
    if (button) button.setAttribute(attributes.expanded, 'false');
    const card = currentTarget.closest('.lq-address-card');
    if (card) card.classList.remove('is-editing');
  };

  _handleDeleteButtonClick = ({ currentTarget }) => {
    if (confirm(currentTarget.getAttribute(attributes.confirmMessage))) {
      Shopify.postLink(currentTarget.dataset.target, { parameters: { _method: 'delete' } });
    }
  };
}

/* ---------- Status badge mapping ---------- */
const STATUS_MAP = {
  paid: 'success', bezahlt: 'success', fulfilled: 'success',
  ausgeliefert: 'success', versandt: 'success', versendet: 'success',
  delivered: 'success', zugestellt: 'success',
  complete: 'success', abgeschlossen: 'success',
  pending: 'warning', ausstehend: 'warning',
  authorized: 'warning', autorisiert: 'warning',
  partial: 'warning', partiell: 'warning',
  partially_paid: 'warning', partially_fulfilled: 'warning',
  in_bearbeitung: 'warning', processing: 'warning',
  unfulfilled: 'warning', on_hold: 'warning',
  refunded: 'danger', erstattet: 'danger', voided: 'danger',
  annulliert: 'danger', cancelled: 'danger', storniert: 'danger',
  failed: 'danger', fehlgeschlagen: 'danger',
  expired: 'danger', abgelaufen: 'danger',
  scheduled: 'info', geplant: 'info', restocked: 'info',
};

function getStatusVariant(text) {
  if (!text) return 'neutral';
  const norm = text.trim().toLowerCase().replace(/\s+/g, '_');
  if (STATUS_MAP[norm]) return STATUS_MAP[norm];
  const noU = text.trim().toLowerCase();
  if (STATUS_MAP[noU]) return STATUS_MAP[noU];
  if (/(paid|bezahlt|fulfill|ausgelie|versand|delivered|zugestellt|abgeschloss)/.test(noU)) return 'success';
  if (/(pending|ausstehend|partial|partiell|process|bearbeitung|unfulfilled)/.test(noU)) return 'warning';
  if (/^nicht /.test(noU)) return 'warning';
  if (/(refund|erstatt|cancel|stornier|void|annull|failed|fehl|expired|abgelaufen)/.test(noU)) return 'danger';
  return 'neutral';
}

function applyStatusBadges(root) {
  (root || document).querySelectorAll('[data-lq-status]').forEach((el) => {
    const text = (el.textContent || '').trim();
    if (!text) return;
    const variant = getStatusVariant(text);
    el.classList.add('lq-badge');
    el.classList.add('lq-badge--' + variant);
  });
}

function setupSidebarToggle() {
  const toggle = document.querySelector('[data-lq-sidebar-toggle]');
  const sidebar = document.querySelector('[data-lq-sidebar]');
  if (!toggle || !sidebar) return;
  toggle.addEventListener('click', function () {
    const isOpen = sidebar.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
}

function setupAuthAnchorFocus() {
  document.querySelectorAll('a[href="#recover"], a[href="#login"]').forEach((link) => {
    link.addEventListener('click', function () {
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (target) {
        setTimeout(function () {
          target.focus({ preventScroll: false });
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }
    });
  });
}

window.addEventListener('DOMContentLoaded', function () {
  if (typeof CustomerAddresses !== 'undefined') new CustomerAddresses();
  applyStatusBadges();
  setupSidebarToggle();
  setupAuthAnchorFocus();
});

/* ---------- Sync nav active-state with URL hash ----------
   Highlights the right sidebar item when user clicks an in-page anchor
   like "#bestellungen" — without this, "Übersicht" stays highlighted
   even after clicking "Bestellungen".
*/
function syncAccountNavActive() {
  const nav = document.querySelector('.customer .lq-nav');
  if (!nav) return;
  const links = nav.querySelectorAll('a');
  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  const currentHash = location.hash || '';

  let best = null;
  let bestScore = -1;

  links.forEach(function (a) {
    const raw = a.getAttribute('href') || '';
    let url;
    try { url = new URL(raw, location.origin); } catch (e) { return; }
    const linkPath = url.pathname.replace(/\/$/, '') || '/';
    const linkHash = url.hash || '';
    if (linkPath !== currentPath) return;

    let score;
    if (currentHash && linkHash === currentHash) score = 3;
    else if (!currentHash && !linkHash) score = 2;
    else if (currentHash && !linkHash) score = 1;
    else score = 0;

    if (score > bestScore) { bestScore = score; best = a; }
  });

  links.forEach(function (a) {
    if (a === best) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAccountNavActive);
} else {
  syncAccountNavActive();
}
window.addEventListener('hashchange', syncAccountNavActive);
